import { Box, Card, CardContent, Typography, Grid, CircularProgress, Chip, Paper } from '@mui/material'
import Apps from '@mui/icons-material/Apps'
import Cancel from '@mui/icons-material/Cancel'
import { styled } from '@mui/material/styles'

const ProductCard = styled(Card)(({ theme, disabled }) => ({
  cursor: 'pointer',
  borderRadius: theme.spacing(2.5),
  background: disabled
    ? 'rgba(255, 255, 255, 0.6)'
    : 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(124, 58, 237, 0.95) 50%, rgba(109, 40, 217, 0.95) 100%)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: `2px solid ${disabled ? 'rgba(0,0,0,0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
  boxShadow: '0 4px 16px rgba(109, 40, 217, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  opacity: disabled ? 0.7 : 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: disabled
      ? 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.05) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.04) 100%)',
    pointerEvents: 'none',
    borderRadius: theme.spacing(2.5),
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 32px rgba(109, 40, 217, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15)',
    borderColor: disabled ? 'rgba(0,0,0,0.2)' : 'rgba(255, 255, 255, 0.3)',
  },
}))

const ProductIcon = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
}))

export default function ProductSelection({
  products,
  onProductSelect,
  loading = false,
  title = 'Select a Product',
  description = 'Choose a product to manage its documentation and help content',
}) {
  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Apps
          sx={{
            fontSize: { xs: 40, sm: 48, md: 56 },
            color: 'primary.main',
            mb: 2,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
          }}
        />
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'text.primary',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {products.map((product) => {
            const isDisabled = product.enabled === false
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <ProductCard disabled={isDisabled} onClick={() => !isDisabled && onProductSelect(product)}>
                  <CardContent
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      p: 3,
                      position: 'relative',
                      zIndex: 1,
                      color: isDisabled ? 'text.secondary' : 'white',
                    }}
                  >
                    <ProductIcon>
                      <Typography
                        variant="h3"
                        sx={{
                          color: isDisabled ? 'text.secondary' : 'white',
                          fontWeight: 700,
                          textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                      >
                        {(product.title || product.packageId || 'P').charAt(0).toUpperCase()}
                      </Typography>
                    </ProductIcon>

                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        mb: 1.5,
                        textShadow: isDisabled ? 'none' : '0 2px 4px rgba(0,0,0,0.2)',
                        color: 'inherit',
                      }}
                    >
                      {product.title || product.packageId}
                    </Typography>

                    {product.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          mb: 2,
                          opacity: 0.9,
                          color: 'inherit',
                          minHeight: '40px',
                        }}
                      >
                        {product.description}
                      </Typography>
                    )}

                    {product.platforms && product.platforms.length > 0 && (
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <Chip
                          label={product.platforms.join(', ')}
                          size="small"
                          sx={{
                            backgroundColor: isDisabled
                              ? 'rgba(0,0,0,0.1)'
                              : 'rgba(255, 255, 255, 0.2)',
                            color: 'inherit',
                            border: `1px solid ${isDisabled ? 'rgba(0,0,0,0.2)' : 'rgba(255, 255, 255, 0.3)'}`,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                    )}

                    {isDisabled && (
                      <Box
                        sx={{
                          mt: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Cancel sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Disabled
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </ProductCard>
              </Grid>
            )
          })}
        </Grid>
      )}

      {!loading && products.length === 0 && (
        <Paper
          sx={{
            textAlign: 'center',
            py: 8,
            px: 3,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Apps sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No products available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Products will appear here once they are configured.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}
